import { useEffect, useState, useRef } from 'react';
import { OcrApi } from '@infra/api_ocr';
import { OcrUser, OcrAnalysis, OcrAnalyzeResponse } from '@domain/ocrModels';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';

type StatusWidget = {
  analysisId: number;
  status: string;
  fileName: string;
};

type WidgetPosition = {
  x: number;
  y: number;
};

export function OcrAnalysePage() {
  const ANALYSES_PAGE_SIZE = 20;

  const [users, setUsers] = useState<OcrUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [userAnalyses, setUserAnalyses] = useState<OcrAnalysis[]>([]);
  const [viewAnalysis, setViewAnalysis] = useState<OcrAnalysis | null>(null);
  const [completedAnalysis, setCompletedAnalysis] = useState<OcrAnalysis | null>(null);
  const [alertsFilter, setAlertsFilter] = useState<'all' | 'with' | 'without'>('all');
  const [analysesOffset, setAnalysesOffset] = useState(0);
  const [analysesHasMore, setAnalysesHasMore] = useState(true);
  const [analysesLoading, setAnalysesLoading] = useState(false);
  const [analysesError, setAnalysesError] = useState<string | null>(null);
  
  const [decryptModalOpen, setDecryptModalOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [statusWidget, setStatusWidget] = useState<StatusWidget | null>(() => {
    const saved = localStorage.getItem('ocr_status_widget');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [widgetPosition, setWidgetPosition] = useState<WidgetPosition>(() => {
    const saved = localStorage.getItem('ocr_status_widget_position');
    if (saved) {
      try {
        const pos = JSON.parse(saved);
        if (pos.x >= 0 && pos.y >= 0 && pos.x < window.innerWidth && pos.y < window.innerHeight) {
          return pos;
        }
      } catch {
      }
    }
    return { x: window.innerWidth - 320, y: window.innerHeight - 150 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const widgetRef = useRef<HTMLDivElement>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedUserId !== null) {
      setAnalysesOffset(0);
      setAnalysesHasMore(true);
      setUserAnalyses([]);
      loadUserAnalyses(selectedUserId, { reset: true });
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (statusWidget !== null && statusWidget.analysisId) {
      const analysisId = statusWidget.analysisId;
      startPolling(analysisId);
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [statusWidget?.analysisId]);

  async function loadUsers() {
    try {
      const data = await OcrApi.listUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }

  async function loadUserAnalyses(
    userId: number,
    options?: {
      reset?: boolean;
    }
  ) {
    const reset = options?.reset ?? false;
    try {
      setAnalysesLoading(true);
      setAnalysesError(null);

      const offset = reset ? 0 : analysesOffset;
      const data = await OcrApi.getUserAnalyses(userId, {
        limit: ANALYSES_PAGE_SIZE,
        offset,
      });

      if (reset) {
        setUserAnalyses(data);
      } else {
        setUserAnalyses(prev => [...prev, ...data]);
      }

      const newOffset = offset + data.length;
      setAnalysesOffset(newOffset);
      setAnalysesHasMore(data.length === ANALYSES_PAGE_SIZE);
    } catch (error) {
      console.error('Failed to load user analyses:', error);
      setAnalysesError('Не удалось загрузить анализы. Попробуйте ещё раз.');
    } finally {
      setAnalysesLoading(false);
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  }

  async function handleDecryptSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedFile || !userName.trim()) {
      alert('Пожалуйста, укажите имя пользователя и выберите файл');
      return;
    }

    setUploading(true);
      try {
        const response = await OcrApi.analyze(selectedFile, userName.trim());
        const widget: StatusWidget = {
          analysisId: response.analysis_id,
          status: response.status || 'обработка...',
          fileName: selectedFile.name
        };
        setStatusWidget(widget);
        localStorage.setItem('ocr_status_widget', JSON.stringify(widget));
      setDecryptModalOpen(false);
      setUserName('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Failed to upload analysis:', error);
      alert('Ошибка при загрузке файла');
    } finally {
      setUploading(false);
    }
  }

  function startPolling(analysisId: number) {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    checkAnalysisStatus(analysisId);

    pollingIntervalRef.current = setInterval(() => {
      checkAnalysisStatus(analysisId);
    }, 5000);
  }

  async function checkAnalysisStatus(analysisId: number) {
    try {
      const analysis = await OcrApi.getAnalysis(analysisId);
      const newStatus = analysis.status || 'обработка...';
      
      setStatusWidget(prev => {
        if (prev && prev.analysisId === analysisId) {
          const updated = {
            ...prev,
            status: newStatus
          };
          localStorage.setItem('ocr_status_widget', JSON.stringify(updated));
          return updated;
        }
        return prev;
      });

      if (analysis.status && ['completed', 'done', 'ready', 'success'].includes(analysis.status.toLowerCase())) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        setStatusWidget(null);
        localStorage.removeItem('ocr_status_widget');
        localStorage.removeItem('ocr_status_widget_position');
        
        setCompletedAnalysis(analysis);
        
        if (selectedUserId !== null) {
          // После завершения анализа перезагружаем список с первой страницы
          setAnalysesOffset(0);
          setAnalysesHasMore(true);
          setUserAnalyses([]);
          loadUserAnalyses(selectedUserId, { reset: true });
        }
      }
    } catch (error) {
      console.error('Failed to check analysis status:', error);
    }
  }

  function handleUserClick(user: OcrUser) {
    if (user.id) {
      setSelectedUserId(user.id);
    }
  }

  function handleAnalysisClick(analysis: OcrAnalysis) {
    setViewAnalysis(analysis);
  }

  function handleLoadMoreAnalyses() {
    if (selectedUserId === null || analysesLoading || !analysesHasMore) {
      return;
    }
    loadUserAnalyses(selectedUserId, { reset: false });
  }

  function hasAlerts(analysis: OcrAnalysis): boolean {
    const alerts = (analysis.result as any)?.alerts ?? analysis.alerts;
    if (!alerts) return false;
    if (Array.isArray(alerts)) {
      return alerts.length > 0;
    }
    return alerts !== null && alerts !== undefined;
  }

  const filteredAnalyses = userAnalyses.filter(analysis => {
    if (alertsFilter === 'all') return true;
    const has = hasAlerts(analysis);
    return alertsFilter === 'with' ? has : !has;
  });

  function closeStatusWidget() {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setStatusWidget(null);
    localStorage.removeItem('ocr_status_widget');
    localStorage.removeItem('ocr_status_widget_position');
  }

  function handleMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).tagName === 'BUTTON' || (e.target as HTMLElement).closest('button')) {
      return;
    }
    if (widgetRef.current) {
      const rect = widgetRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  }

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        const maxX = window.innerWidth - (widgetRef.current?.offsetWidth || 300);
        const maxY = window.innerHeight - (widgetRef.current?.offsetHeight || 150);
        
        const constrainedX = Math.max(0, Math.min(newX, maxX));
        const constrainedY = Math.max(0, Math.min(newY, maxY));
        
        const newPosition = { x: constrainedX, y: constrainedY };
        setWidgetPosition(newPosition);
        localStorage.setItem('ocr_status_widget_position', JSON.stringify(newPosition));
      }
    }

    function handleMouseUp() {
      setIsDragging(false);
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  return (
    <div>
      {statusWidget && (
        <div 
          ref={widgetRef}
          style={{
            position: 'fixed',
            left: widgetPosition.x,
            top: widgetPosition.y,
            background: 'var(--card-background)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 16,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            minWidth: 300,
            maxWidth: 400,
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none'
          }}
          onMouseDown={handleMouseDown}
        >
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8}}>
            <div style={{fontWeight: 'bold', fontSize: 14, cursor: 'grab'}}>Расшифровка анализа</div>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                closeStatusWidget();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 18,
                padding: 0,
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text)',
                flexShrink: 0
              }}
            >
              ×
            </button>
          </div>
          <div style={{fontSize: 12, color: 'var(--muted)', marginBottom: 8, wordBreak: 'break-word'}}>
            {statusWidget.fileName}
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <span style={{fontSize: 14}}>Статус: {statusWidget.status}</span>
            {!['completed', 'done', 'ready', 'success'].includes(statusWidget.status.toLowerCase()) && (
              <span style={{color: 'var(--muted)'}}>⏳</span>
            )}
          </div>
        </div>
      )}

      <div className="toolbar">
        <div className="title"><h2 style={{margin:0}}>Расшифровка анализов (OCR)</h2></div>
        <button onClick={() => setDecryptModalOpen(true)}>
          Расшифровать анализ
        </button>
      </div>

      <div className="row">
        <div style={{flex: 1}}>
          <DataTable
            data={users}
            title="Пользователи"
            searchFields={['name']}
            searchPlaceholder="Поиск по имени..."
            onRowClick={handleUserClick}
            columns={[
              { 
                key: 'id', 
                title: 'ID',
                render: (item) => item.id
              },
              { 
                key: 'user', 
                title: 'Имя',
                render: (item) => item.user || '-'
              }
            ]}
          />
        </div>

        {selectedUserId !== null && (
          <div style={{flex: 2}}>
            <div style={{marginBottom: 8, display: 'flex', justifyContent: 'flex-end'}}>
              <select 
                value={alertsFilter} 
                onChange={e => setAlertsFilter(e.target.value as 'all' | 'with' | 'without')}
                style={{width:200}}
              >
                <option value="all">Все анализы</option>
                <option value="with">С отклонениями</option>
                <option value="without">Без отклонений</option>
              </select>
            </div>
            <DataTable
              data={filteredAnalyses}
              title={`Анализы пользователя #${selectedUserId}`}
              searchFields={['status']}
              searchPlaceholder="Поиск по статусу..."
              onRowClick={handleAnalysisClick}
              columns={[
                { 
                  key: 'id', 
                  title: 'ID'
                },
                { 
                  key: 'status', 
                  title: 'Статус',
                  render: (item) => item.status || 'неизвестно'
                },
                { 
                  key: 'created_at', 
                  title: 'Создан',
                  render: (item) => {
                    if (!item.created_at) return '-';
                    try {
                      return new Date(item.created_at).toLocaleString('ru-RU');
                    } catch {
                      return item.created_at;
                    }
                  }
                },
                { 
                  key: 'alerts', 
                  title: 'Отклонения',
                  render: (item) => {
                    const has = hasAlerts(item);
                    return has ? 'найдены' : '-';
                  }
                }
              ]}
            />
            <div style={{marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <span style={{color: 'var(--muted)'}}>
                Показано {filteredAnalyses.length} анализов
              </span>
              <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                {analysesError && (
                  <span style={{color: 'var(--danger, #dc3545)', fontSize: 12}}>
                    {analysesError}
                  </span>
                )}
                <button
                  className="secondary"
                  onClick={handleLoadMoreAnalyses}
                  disabled={!analysesHasMore || analysesLoading}
                >
                  {analysesLoading ? 'Загрузка...' : analysesHasMore ? 'Загрузить ещё' : 'Больше нет'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {decryptModalOpen && (
        <Modal title="Расшифровать анализ" onClose={() => {
          setDecryptModalOpen(false);
          setUserName('');
          setSelectedFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}>
          <form onSubmit={handleDecryptSubmit}>
            <div style={{marginBottom: 16}}>
              <label htmlFor="user-name">Имя пользователя</label>
              <input
                id="user-name"
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Введите имя пользователя"
                required
                disabled={uploading}
              />
            </div>
            <div style={{marginBottom: 16}}>
              <label htmlFor="decrypt-file">Файл анализа</label>
              <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {selectedFile ? 'Изменить файл' : 'Выбрать файл'}
                </button>
                {selectedFile && (
                  <span style={{fontSize: 14, color: 'var(--muted)'}}>
                    {selectedFile.name}
                  </span>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                style={{display: 'none'}}
                disabled={uploading}
              />
            </div>
            <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24}}>
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setDecryptModalOpen(false);
                  setUserName('');
                  setSelectedFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                disabled={uploading}
              >
                Отмена
              </button>
              <button type="submit" disabled={uploading || !selectedFile || !userName.trim()}>
                {uploading ? 'Отправка...' : 'Отправить на расшифровку'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {completedAnalysis && (
        <Modal title="Расшифровка анализа завершена" onClose={() => setCompletedAnalysis(null)}>
          <div style={{maxHeight: '70vh', overflow: 'auto'}}>
            <div style={{
              padding: 16,
              background: 'var(--success-bg, #d4edda)',
              borderRadius: 8,
              marginBottom: 16,
              color: 'var(--success-text, #155724)',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{fontSize: 20}}>✓</span>
              <span style={{fontWeight: 'bold'}}>Анализ успешно расшифрован</span>
            </div>
            
            {completedAnalysis.created_at && (
              <div style={{marginBottom: 16, fontSize: 14, color: 'var(--muted)'}}>
                <strong>Дата:</strong> {new Date(completedAnalysis.created_at).toLocaleString('ru-RU')}
              </div>
            )}
            
            {completedAnalysis.analyses_name && (
              <div style={{marginBottom: 16, fontSize: 14, color: 'var(--muted)'}}>
                <strong>Анализ:</strong> {completedAnalysis.analyses_name}
              </div>
            )}

            {completedAnalysis.summary && (
              <div style={{marginBottom: 16}}>
                <strong style={{fontSize: 16, display: 'block', marginBottom: 8}}>Расшифровка анализа:</strong>
                <div style={{
                  background: 'var(--surface)',
                  padding: 16,
                  borderRadius: 8,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  border: '1px solid var(--border)'
                }}>
                  {completedAnalysis.summary}
                </div>
              </div>
            )}
            
            {completedAnalysis.comments && (
              <div style={{marginBottom: 16}}>
                <strong style={{fontSize: 16, display: 'block', marginBottom: 8}}>Рекомендации:</strong>
                <div style={{
                  background: 'var(--surface)',
                  padding: 16,
                  borderRadius: 8,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  border: '1px solid var(--border)'
                }}>
                  {completedAnalysis.comments}
                </div>
              </div>
            )}
            
            {!completedAnalysis.summary && !completedAnalysis.comments && (
              <div style={{padding: 16, textAlign: 'center', color: 'var(--muted)'}}>
                Результаты анализа пока недоступны
              </div>
            )}
          </div>
          <div style={{marginTop: 16, display: 'flex', justifyContent: 'flex-end'}}>
            <button onClick={() => setCompletedAnalysis(null)}>Закрыть</button>
          </div>
        </Modal>
      )}

      {viewAnalysis && (() => {
        const alerts = (viewAnalysis.result as any)?.alerts ?? viewAnalysis.alerts;
        const hasAlerts = alerts && (Array.isArray(alerts) ? alerts.length > 0 : alerts !== null && alerts !== undefined);
        
        return (
          <Modal title={`Анализ #${viewAnalysis.id}`} onClose={() => setViewAnalysis(null)}>
            <div style={{maxHeight: '70vh', overflow: 'auto'}}>
              {/* Индикатор отклонений */}
              {hasAlerts ? (
                <div style={{
                  padding: 16,
                  background: '#fff3cd',
                  borderRadius: 8,
                  marginBottom: 16,
                  border: '1px solid #ffc107',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12
                }}>
                  <span style={{fontSize: 24}}>⚠️</span>
                  <span style={{fontWeight: 'bold', color: '#856404'}}>Обнаружены отклонения от норм</span>
                </div>
              ) : (
                <div style={{
                  padding: 16,
                  background: '#d4edda',
                  borderRadius: 8,
                  marginBottom: 16,
                  border: '1px solid #28a745',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12
                }}>
                  <span style={{fontSize: 24}}>✓</span>
                  <span style={{fontWeight: 'bold', color: '#155724'}}>Все показатели в норме</span>
                </div>
              )}
              
              <div style={{marginBottom: 16}}>
                <strong>Статус:</strong> {viewAnalysis.status || 'неизвестно'}
              </div>
            {viewAnalysis.created_at && (
              <div style={{marginBottom: 16}}>
                <strong>Создан:</strong> {new Date(viewAnalysis.created_at).toLocaleString('ru-RU')}
              </div>
            )}
            {viewAnalysis.summary && (
              <div style={{marginBottom: 16}}>
                <strong>Расшифровка анализа:</strong>
                <div style={{
                  background: 'var(--surface)',
                  padding: 16,
                  borderRadius: 8,
                  marginTop: 8,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {viewAnalysis.summary}
                </div>
              </div>
            )}
            {viewAnalysis.comments && (
              <div style={{marginBottom: 16}}>
                <strong>Рекомендации:</strong>
                <div style={{
                  background: 'var(--surface)',
                  padding: 16,
                  borderRadius: 8,
                  marginTop: 8,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {viewAnalysis.comments}
                </div>
              </div>
            )}

            {/* Блок отклонений */}
            {(() => {
              const alerts = (viewAnalysis.result as any)?.alerts ?? viewAnalysis.alerts;
              if (!alerts || (Array.isArray(alerts) && alerts.length === 0) || alerts === null) {
                return null;
              }
              
              const alertsArray = Array.isArray(alerts) ? alerts : [alerts];
              
              return (
                <div style={{marginBottom: 16}}>
                  <strong style={{fontSize: 16, display: 'block', marginBottom: 8}}>Отклонения от норм:</strong>
                  <div style={{
                    background: '#fff3cd',
                    padding: 16,
                    borderRadius: 8,
                    border: '1px solid #ffc107'
                  }}>
                    {alertsArray.map((alert: any, idx: number) => (
                      <div key={idx} style={{
                        marginBottom: idx < alertsArray.length - 1 ? 12 : 0,
                        paddingBottom: idx < alertsArray.length - 1 ? 12 : 0,
                        borderBottom: idx < alertsArray.length - 1 ? '1px solid #ffc107' : 'none'
                      }}>
                        {typeof alert === 'string' ? (
                          <div style={{color: '#856404', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                            {alert}
                          </div>
                        ) : (
                          <div style={{color: '#856404'}}>
                            {JSON.stringify(alert, null, 2)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Блок объяснения модели */}
            {(() => {
              const resume = (viewAnalysis.result as any)?.resume ?? viewAnalysis.resume;
              if (!resume) {
                return null;
              }
              
              return (
                <div style={{marginBottom: 16}}>
                  <strong style={{fontSize: 16, display: 'block', marginBottom: 8}}>Объяснение модели:</strong>
                  <div style={{
                    background: 'var(--surface)',
                    padding: 16,
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    lineHeight: '1.6'
                  }}>
                    {resume}
                  </div>
                </div>
              );
            })()}

            {viewAnalysis.result && (() => {
              try {
                let jsonData: any;
                const resultValue = (viewAnalysis.result as any)?.result ?? viewAnalysis.result;
                
                if (typeof resultValue === 'string') {
                  jsonData = JSON.parse(resultValue);
                } else if (typeof resultValue === 'object' && resultValue !== null) {
                  jsonData = resultValue;
                } else {
                  return null;
                }
                
                const renderJsonValue = (value: any, depth: number = 0): React.ReactNode => {
                  const indent = depth * 20;
                  
                  if (value === null) {
                    return <span style={{color: '#999', fontStyle: 'italic'}}>null</span>;
                  }
                  
                  if (value === undefined) {
                    return <span style={{color: '#999', fontStyle: 'italic'}}>undefined</span>;
                  }
                  
                  if (typeof value === 'boolean') {
                    return <span style={{color: '#0066cc'}}>{String(value)}</span>;
                  }
                  
                  if (typeof value === 'number') {
                    return <span style={{color: '#0066cc'}}>{value.toLocaleString('ru-RU')}</span>;
                  }
                  
                  if (typeof value === 'string') {
                    return <span style={{color: '#008000'}}>"{value}"</span>;
                  }
                  
                  if (Array.isArray(value)) {
                    if (value.length === 0) {
                      return <span style={{color: '#999'}}>[]</span>;
                    }
                    return (
                      <div style={{marginLeft: indent}}>
                        <span style={{color: '#999'}}>[</span>
                        {value.map((item, idx) => (
                          <div key={idx} style={{marginLeft: 20, paddingLeft: 8, borderLeft: '1px solid var(--border)'}}>
                            {renderJsonValue(item, depth + 1)}
                            {idx < value.length - 1 && <span style={{color: '#999'}}>,</span>}
                          </div>
                        ))}
                        <span style={{color: '#999'}}>]</span>
                      </div>
                    );
                  }
                  
                  if (typeof value === 'object') {
                    const entries = Object.entries(value);
                    if (entries.length === 0) {
                      return <span style={{color: '#999'}}>{'{}'}</span>;
                    }
                    return (
                      <div style={{marginLeft: indent}}>
                        <span style={{color: '#999'}}>{'{'}</span>
                        {entries.map(([key, val], idx) => (
                          <div key={key} style={{marginLeft: 20, paddingLeft: 8, borderLeft: '1px solid var(--border)'}}>
                            <span style={{color: '#881391', fontWeight: 'bold'}}>"{key}"</span>
                            <span style={{color: '#999'}}>: </span>
                            {renderJsonValue(val, depth + 1)}
                            {idx < entries.length - 1 && <span style={{color: '#999'}}>,</span>}
                          </div>
                        ))}
                        <span style={{color: '#999'}}>{'}'}</span>
                      </div>
                    );
                  }
                  
                  return <span>{String(value)}</span>;
                };
                
                return (
                  <div style={{marginBottom: 16}}>
                    <strong style={{fontSize: 16, display: 'block', marginBottom: 8}}>Оцифрованный анализ:</strong>
                    <div style={{
                      background: 'var(--surface)',
                      padding: 16,
                      borderRadius: 8,
                      border: '1px solid var(--border)',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      overflow: 'auto',
                      maxHeight: '400px'
                    }}>
                      {renderJsonValue(jsonData)}
                    </div>
                  </div>
                );
              } catch (error) {
                // Если не удалось распарсить JSON, показываем как текст
                const resultValue = (viewAnalysis.result as any)?.result ?? viewAnalysis.result;
                return (
                  <div style={{marginBottom: 16}}>
                    <strong style={{fontSize: 16, display: 'block', marginBottom: 8}}>Оцифрованный анализ:</strong>
                    <div style={{
                      background: 'var(--surface)',
                      padding: 16,
                      borderRadius: 8,
                      border: '1px solid var(--border)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                      fontSize: '13px'
                    }}>
                      {typeof resultValue === 'string' ? resultValue : JSON.stringify(resultValue, null, 2)}
                    </div>
                  </div>
                );
              }
            })()}
          </div>
          <div style={{marginTop: 16}}>
            <button className="secondary" onClick={() => setViewAnalysis(null)}>Закрыть</button>
          </div>
        </Modal>
        );
      })()}
    </div>
  );
}
